import React, { createContext, useContext, useState, useCallback, useRef } from "react";
import { getBackendUrl } from "../utils/backendUrl";
import { useAuth } from "./AuthContext";

const ChatContext = createContext(null);

export function ChatProvider({ children }) {
  const { token } = useAuth();
  const [conversations, setConversations] = useState([]);
  const [activeConversation, setActiveConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [isStreaming, setIsStreaming] = useState(false);
  const [lastImageUrl, setLastImageUrl] = useState(null);
  const [streamingContent, setStreamingContent] = useState("");
  const [streamingMeta, setStreamingMeta] = useState(null);

  const streamBufferRef = useRef("");
  const rafIdRef = useRef(null);

  const backendUrl = getBackendUrl();
  const headers = { "Content-Type": "application/json", Authorization: `Bearer ${token}` };

  const flushStreamBuffer = useCallback(() => {
    setStreamingContent(streamBufferRef.current);
    rafIdRef.current = null;
  }, []);

  const scheduleFlush = useCallback(() => {
    if (rafIdRef.current === null) {
      rafIdRef.current = requestAnimationFrame(flushStreamBuffer);
    }
  }, [flushStreamBuffer]);

  const loadConversations = useCallback(async () => {
    if (!token) return;
    try {
      const res = await fetch(`${backendUrl}/conversations`, { headers });
      if (res.ok) setConversations(await res.json());
    } catch (e) {
      console.error("Failed to load conversations:", e);
    }
  }, [token, backendUrl]);

  const createConversation = async () => {
    const res = await fetch(`${backendUrl}/conversations`, { method: "POST", headers });
    if (res.ok) {
      const conv = await res.json();
      setActiveConversation(conv);
      setMessages([]);
      await loadConversations();
      return conv;
    }
  };

  const loadMessages = async (convId) => {
    const res = await fetch(`${backendUrl}/conversations/${convId}/messages`, { headers });
    if (res.ok) {
      const msgs = await res.json();
      setMessages(msgs);
      for (let i = msgs.length - 1; i >= 0; i--) {
        if (msgs[i].imageUrl) {
          setLastImageUrl(msgs[i].imageUrl);
          break;
        }
      }
    }
  };

  const sendMessage = async (text, action = null, files = null) => {
    let conv = activeConversation;
    if (!conv) {
      conv = await createConversation();
      if (!conv) return;
    }

    const convId = conv.id;

    const userMsg = { role: "user", content: text, timestamp: new Date().toISOString() };
    if (files?.length) {
      userMsg.files = files.map((f) => f.name);
      userMsg.imageUrls = files
        .filter((f) => f.fileType === "image" && f.data)
        .map((f) => `data:${f.type};base64,${f.data}`);
    }
    setMessages((prev) => [...prev, userMsg]);

    streamBufferRef.current = "";
    setStreamingContent("");
    setStreamingMeta(null);
    setIsStreaming(true);

    try {
      const endpoint = files?.length ? "/chat/upload/stream" : "/chat/stream";
      const body = files?.length
        ? { message: text, conversationId: convId, action, lastImageUrl, files: files.map((f) => ({ name: f.name, type: f.type, fileType: f.fileType || "image", data: f.data, size: f.size })) }
        : { message: text, conversationId: convId, action, lastImageUrl };

      const res = await fetch(`${backendUrl}${endpoint}`, {
        method: "POST",
        headers,
        body: JSON.stringify(body),
      });

      if (!res.ok) {
        if (res.status === 402) {
          throw new Error("NO_CREDITS");
        }
        throw new Error(`HTTP ${res.status}`);
      }

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let imageUrl = null;
      let sseBuffer = ""; // Buffer for incomplete SSE lines

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        sseBuffer += decoder.decode(value, { stream: true });

        // Process complete lines only (split on double newline for SSE)
        const parts = sseBuffer.split("\n");
        // Keep the last part as it might be incomplete
        sseBuffer = parts.pop() || "";

        for (const line of parts) {
          const trimmed = line.trim();
          if (!trimmed.startsWith("data: ")) continue;
          try {
            const data = JSON.parse(trimmed.slice(6));
            if (data.type === "token") {
              streamBufferRef.current += data.text;
              scheduleFlush();
            } else if (data.type === "image") {
              imageUrl = data.imageUrl;
              setLastImageUrl(imageUrl);
              setStreamingMeta((prev) => ({ ...prev, imageUrl }));
            } else if (data.type === "image_generating") {
              setStreamingMeta((prev) => ({ ...prev, imageGenerating: true }));
            } else if (data.type === "error") {
              console.error("Stream error from server:", data.message);
            }
          } catch (e) {
            // Only log if it looks like it should have been valid JSON
            if (trimmed.length > 6) {
              console.warn("SSE parse error:", trimmed.slice(0, 100), e.message);
            }
          }
        }
      }

      // Process any remaining buffer
      if (sseBuffer.trim().startsWith("data: ")) {
        try {
          const data = JSON.parse(sseBuffer.trim().slice(6));
          if (data.type === "image") {
            imageUrl = data.imageUrl;
            setLastImageUrl(imageUrl);
          }
        } catch {}
      }

      // Finalize — clear streaming state BEFORE adding final message to avoid duplicate image
      if (rafIdRef.current !== null) {
        cancelAnimationFrame(rafIdRef.current);
        rafIdRef.current = null;
      }
      const finalContent = streamBufferRef.current;
      const finalMsg = { role: "assistant", content: finalContent, timestamp: new Date().toISOString() };
      if (imageUrl) finalMsg.imageUrl = imageUrl;
      setStreamingMeta(null);
      setStreamingContent("");
      setIsStreaming(false);
      setMessages((prev) => [...prev, finalMsg]);
      streamBufferRef.current = "";
      loadConversations();
    } catch (e) {
      console.error("Stream error:", e);
      if (rafIdRef.current !== null) {
        cancelAnimationFrame(rafIdRef.current);
        rafIdRef.current = null;
      }
      const partialContent = streamBufferRef.current;
      setStreamingMeta(null);
      setStreamingContent("");
      setIsStreaming(false);
      if (e.message === "NO_CREDITS") {
        // Remove the optimistic user message
        setMessages((prev) => prev.slice(0, -1));
      } else {
        setMessages((prev) => [...prev, {
          role: "assistant",
          content: partialContent || "Entschuldigung, es ist ein Fehler aufgetreten.",
          timestamp: new Date().toISOString(),
        }]);
      }
      streamBufferRef.current = "";
      loadConversations();
      if (e.message === "NO_CREDITS") throw e;
    }
  };

  return (
    <ChatContext.Provider
      value={{
        conversations,
        activeConversation,
        setActiveConversation,
        messages,
        setMessages,
        isStreaming,
        lastImageUrl,
        streamingContent,
        streamingMeta,
        loadConversations,
        createConversation,
        loadMessages,
        sendMessage,
      }}
    >
      {children}
    </ChatContext.Provider>
  );
}

export const useChat = () => useContext(ChatContext);
